import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { LoginInput, MeResponse, SessionUser } from '@app/shared';
import { AUTH_ENDED_EVENT, ApiError, api } from '../../lib/api';

interface AuthState {
  status: 'loading' | 'signedIn' | 'signedOut';
  user: SessionUser | null;
  idleMinutes: number;
  /** True when the user's group holds the permission code. */
  can: (code: string) => boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const ME_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const me = useQuery<MeResponse | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  // Drops every cached query except the session itself, so one user never sees another's data.
  // (`queryClient.clear()` would also detach the mounted session query from its observer.)
  // The customer portal has a session of its own, which a staff session ending does not touch.
  const dropUserData = useCallback(
    () =>
      queryClient.removeQueries({
        predicate: (q) => q.queryKey[0] !== ME_KEY[0] && q.queryKey[0] !== 'portal',
      }),
    [queryClient],
  );

  // Any API call can discover that the session is gone (idle timeout, takeover, lock).
  useEffect(() => {
    const onEnded = () => {
      queryClient.setQueryData(ME_KEY, null);
      dropUserData();
    };
    window.addEventListener(AUTH_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(AUTH_ENDED_EVENT, onEnded);
  }, [queryClient, dropUserData]);

  const login = useCallback(
    async (input: LoginInput) => {
      const result = await api.post<MeResponse>('/auth/login', input);
      dropUserData();
      queryClient.setQueryData(ME_KEY, result);
    },
    [queryClient, dropUserData],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // The session may already be gone; the local sign-out below still applies.
    }
    queryClient.setQueryData(ME_KEY, null);
    dropUserData();
  }, [queryClient, dropUserData]);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(me.data?.permissions ?? []);
    return {
      status: me.isPending ? 'loading' : me.data ? 'signedIn' : 'signedOut',
      user: me.data?.user ?? null,
      idleMinutes: me.data?.idleMinutes ?? 30,
      can: (code) => permissions.has(code),
      login,
      logout,
    };
  }, [me.isPending, me.data, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
